import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  IconButton,
  Snackbar,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import EuroIcon from '@mui/icons-material/Euro';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import PrintIcon from '@mui/icons-material/Print';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';

import { meterReadingsApi, plantsApi, metersApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';
import ReadingSheetPreview from './ReadingSheetPreview';

// Costanti
const MC_TO_LITERS = 4.15;

// Formattazione numeri
const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return parseFloat(num).toLocaleString('it-IT', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Formattazione valuta
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Formattazione data
const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT');
};

// Componente per storico letture espandibile
const MeterHistoryPanel = ({ meterId, isOpen }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['meter-history', meterId],
    queryFn: async () => {
      const res = await meterReadingsApi.getHistory(meterId, { limit: 20 });
      return res.data?.data || {};
    },
    enabled: isOpen && !!meterId,
    staleTime: 60000
  });

  if (!isOpen) return null;

  const readings = data?.readings || [];
  const stats = data?.stats || {};

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>Caricamento storico...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 1 }}>Errore nel caricamento dello storico</Alert>
      ) : readings.length === 0 ? (
        <Alert severity="info">Nessuna lettura storica disponibile per questo contatore</Alert>
      ) : (
        <>
          {/* Statistiche rapide */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip 
              icon={<HistoryIcon />}
              label={`${stats.num_letture || 0} letture`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip 
              icon={<SpeedIcon />}
              label={`Media: ${formatNumber(stats.consumo_medio_mc, 2)} MC/bimestre`}
              size="small"
              color="info"
              variant="outlined"
            />
            <Chip 
              label={`Totale: ${formatNumber(stats.consumo_totale_mc, 0)} MC`}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Tabella storico */}
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Data</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Lettura MC</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Consumo MC</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Consumo Lt</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Periodo</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Importo</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Fattura</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {readings.map((r, idx) => (
                  <TableRow 
                    key={r.id} 
                    sx={{ 
                      bgcolor: idx === 0 ? 'primary.50' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={idx === 0 ? 'bold' : 'normal'}>
                        {formatDate(r.data_lettura)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatNumber(r.lettura_mc, 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={r.is_anomaly ? 'warning.main' : 'text.primary'}
                        fontWeight="medium"
                      >
                        {formatNumber(r.consumo_mc, 2)}
                        {r.is_anomaly && <WarningIcon sx={{ ml: 0.5, fontSize: 14, verticalAlign: 'middle' }} color="warning" />}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {formatNumber(r.consumo_litri, 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.periodo_riferimento || `${r.anno}/${r.bimestre}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" color="primary.main">
                        {r.importo_totale ? formatCurrency(r.importo_totale) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.invoice ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={`#${formatNuf(r.invoice.sezionale, r.invoice.nuf)}`}
                            size="small"
                            color={r.invoice.stato === 'pagata' ? 'success' : 'default'}
                            variant="outlined"
                          />
                          {r.invoice.insoluto > 0 && (
                            <Chip 
                              label={formatCurrency(r.invoice.insoluto)}
                              size="small"
                              color="error"
                              variant="filled"
                            />
                          )}
                        </Box>
                      ) : r.da_fatturare === false ? (
                        <Chip 
                          label="non Fatturata"
                          size="small"
                          color="warning"
                          variant="filled"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

// Componente riga contatore per inserimento lettura (con storico espandibile)
// reading è un oggetto: { value, tipoLettura, daFatturare } oppure null/undefined
const MeterRow = ({ meter, reading, onReadingChange }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Estrai i valori dall'oggetto reading
  const readingValue = reading?.value;
  const tipoLettura = reading?.tipoLettura || 'reale';
  const daFatturare = reading?.daFatturare !== undefined ? reading.daFatturare : true;
  
  // Calcola consumo e importo in tempo reale
  const consumoMc = useMemo(() => {
    if (readingValue === '' || readingValue === null || readingValue === undefined) return null;
    return parseFloat(readingValue) - parseFloat(meter.ultima_lettura_mc || 0);
  }, [readingValue, meter.ultima_lettura_mc]);

  const consumoLitri = consumoMc !== null ? consumoMc * MC_TO_LITERS : null;
  
  const importoStimato = useMemo(() => {
    if (consumoLitri === null || !meter.prezzo_litro) return null;
    const gas = consumoLitri * parseFloat(meter.prezzo_litro);
    const iva = gas * (parseFloat(meter.aliquota_iva || 22) / 100);
    return gas + iva;
  }, [consumoLitri, meter.prezzo_litro, meter.aliquota_iva]);

  // Anomalia: consumo > 2x media
  const isAnomaly = consumoMc !== null && 
    meter.consumo_medio_mc > 0 && 
    consumoMc > meter.consumo_medio_mc * 2;

  // Consumo negativo
  const isNegative = consumoMc !== null && consumoMc < 0;

  // Handler per cambio valore lettura
  const handleValueChange = (e) => {
    const value = e.target.value;
    console.log('[TIPO_LETTURA] handleValueChange chiamato per meter:', meter.id, '| nuovo valore:', value, '| tipoLettura attuale:', tipoLettura);
    if (value === '') {
      onReadingChange(meter.id, null);
    } else {
      const nuovaLettura = parseFloat(value);
      const ultimaLettura = parseFloat(meter.ultima_lettura_mc || 0);
      const consumo = nuovaLettura - ultimaLettura;
      
      // Aggiorna automaticamente daFatturare in base al consumo:
      // - Se consumo <= 0: imposta false
      // - Se consumo > 0: imposta true
      const autoDaFatturare = consumo > 0;
      
      console.log('[TIPO_LETTURA] Salvo con tipoLettura:', tipoLettura, '| consumo:', consumo, '| daFatturare:', autoDaFatturare);
      onReadingChange(meter.id, { 
        value: nuovaLettura, 
        tipoLettura, 
        daFatturare: autoDaFatturare 
      });
    }
  };
  
  // Handler per cambio tipo lettura
  const handleTipoChange = (e) => {
    console.log('[TIPO_LETTURA] handleTipoChange chiamato per meter:', meter.id, '| nuovo tipo:', e.target.value, '| valore attuale:', readingValue);
    // Aggiorna sempre il tipo, anche se il valore è vuoto
    onReadingChange(meter.id, { 
      value: readingValue, 
      tipoLettura: e.target.value, 
      daFatturare 
    });
  };
  
  // Handler per cambio da fatturare
  const handleDaFatturareChange = (e) => {
    // Aggiorna sempre da_fatturare, anche se il valore è vuoto
    onReadingChange(meter.id, { 
      value: readingValue, 
      tipoLettura, 
      daFatturare: e.target.checked 
    });
  };

  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <>
      <TableRow 
        sx={{ 
          bgcolor: readingValue !== null && readingValue !== undefined ? (daFatturare ? 'success.50' : 'warning.50') : 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
          '& > *': { borderBottom: expanded ? 'none' : undefined }
        }}
      >
        {/* Pulsante espansione storico */}
        <TableCell sx={{ width: 50 }}>
          <Tooltip title={expanded ? "Chiudi storico" : "Mostra storico letture"}>
            <IconButton 
              size="small" 
              onClick={toggleExpanded}
              color={expanded ? "primary" : "default"}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </TableCell>

        {/* Progressivo */}
        <TableCell>
          <Typography variant="body2" fontWeight="bold">
            {meter.sotto_zona || meter.c_sotto_zo || meter.c_nu_conta || '-'}
          </Typography>
        </TableCell>

        {/* Contatore */}
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {meter.meter_number}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {meter.api_code || '-'}
            </Typography>
          </Box>
        </TableCell>

        {/* Cliente */}
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
              {meter.customer_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {meter.customer_code} - {meter.customer_location}
            </Typography>
          </Box>
        </TableCell>

        {/* Insoluto cliente */}
        <TableCell align="right">
          {meter.insoluto_cliente > 0 ? (
            <Chip 
              label={formatCurrency(meter.insoluto_cliente)} 
              size="small" 
              color="error" 
              variant="outlined"
            />
          ) : (
            <Chip label="OK" size="small" color="success" variant="outlined" />
          )}
        </TableCell>

        {/* Ultima lettura */}
        <TableCell align="right">
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {formatNumber(meter.ultima_lettura_mc, 0)} MC
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(meter.data_ultima_lettura)}
            </Typography>
          </Box>
        </TableCell>

        {/* Consumo medio */}
        <TableCell align="right">
          <Typography variant="body2" color="text.secondary">
            {formatNumber(meter.consumo_medio_mc, 2)} MC
          </Typography>
        </TableCell>

        {/* Nuova lettura (input) */}
        <TableCell>
          <TextField
            type="number"
            value={readingValue !== null && readingValue !== undefined ? readingValue : ''}
            onChange={handleValueChange}
            size="small"
            placeholder="Nuova lettura MC"
            inputProps={{ step: 0.01, min: 0 }}
            sx={{ 
              width: 130,
              '& .MuiOutlinedInput-root': {
                bgcolor: readingValue !== null && readingValue !== undefined ? (daFatturare ? 'success.50' : 'warning.50') : 'inherit',
                '& fieldset': {
                  borderColor: isAnomaly ? 'warning.main' : isNegative ? 'error.main' : undefined,
                  borderWidth: isAnomaly || isNegative ? 2 : 1
                }
              }
            }}
            InputProps={{
              endAdornment: (
                <>
                  {isAnomaly && (
                    <Tooltip title="Consumo anomalo (> 2x media)">
                      <WarningIcon color="warning" fontSize="small" />
                    </Tooltip>
                  )}
                  {isNegative && (
                    <Tooltip title="Consumo negativo! Verifica la lettura">
                      <WarningIcon color="error" fontSize="small" />
                    </Tooltip>
                  )}
                  {readingValue !== null && readingValue !== undefined && !isAnomaly && !isNegative && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                </>
              )
            }}
          />
        </TableCell>

        {/* Tipo lettura (Reale/Stimata) */}
        <TableCell sx={{ minWidth: 100 }}>
          <Select
            value={tipoLettura}
            onChange={handleTipoChange}
            size="small"
            sx={{ 
              minWidth: 95,
              fontSize: '0.8rem',
              '& .MuiSelect-select': { py: 0.8 }
            }}
          >
            <MenuItem value="reale">Reale</MenuItem>
            <MenuItem value="stimata">Stimata</MenuItem>
          </Select>
        </TableCell>

        {/* Da fatturare (switch) */}
        <TableCell align="center">
          <Tooltip title={daFatturare ? 'Verrà fatturata' : 'Solo registrazione, no fattura'}>
            <Switch
              checked={daFatturare}
              onChange={handleDaFatturareChange}
              size="small"
              color={daFatturare ? 'success' : 'warning'}
            />
          </Tooltip>
        </TableCell>

        {/* Consumo calcolato */}
        <TableCell align="right">
          {consumoMc !== null ? (
            <Box>
              <Typography 
                variant="body2" 
                fontWeight="bold"
                color={isNegative ? 'error.main' : isAnomaly ? 'warning.main' : 'success.main'}
              >
                {formatNumber(consumoMc, 2)} MC
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatNumber(consumoLitri, 0)} L
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* Importo stimato */}
        <TableCell align="right">
          {importoStimato !== null ? (
            <Typography variant="body2" fontWeight="bold" color="primary.main">
              {formatCurrency(importoStimato)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
      </TableRow>
      
      {/* Riga collapsabile con storico */}
      <TableRow>
        <TableCell colSpan={13} sx={{ p: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <MeterHistoryPanel meterId={meter.id} isOpen={expanded} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Componente principale
const ReadingEntryTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  // State
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [plantInputValue, setPlantInputValue] = useState('');
  const [readings, setReadings] = useState({}); // { meterId: { value, tipoLettura, daFatturare } }
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialogo conferma data lettura
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [dataLettura, setDataLettura] = useState(new Date().toISOString().split('T')[0]);
  
  // Livello impianto (serbatoio)
  const [livelloPercentuale, setLivelloPercentuale] = useState('');
  const [livelloLitri, setLivelloLitri] = useState('');

  // Stato ordinamento tabella
  const [orderBy, setOrderBy] = useState('sotto_zona');
  const [order, setOrder] = useState('asc');

  // Handler per ordinamento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Dialog stampa tabulato
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printMode, setPrintMode] = useState('plant'); // 'plant' o 'provincia'
  const [printProvincia, setPrintProvincia] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [showInsoluto, setShowInsoluto] = useState(true); // Mostra colonna insoluti

  // Preview tabulato
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewParams, setPreviewParams] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Query per province disponibili (globale)
  const filterOptionsQuery = useQuery({
    queryKey: ['meters-filter-options'],
    queryFn: async () => {
      const response = await metersApi.getFilterOptions();
      return response.data || {};
    },
    staleTime: 300000 // 5 minuti
  });
  const globalProvince = useMemo(() => filterOptionsQuery.data?.provincia || [], [filterOptionsQuery.data]);

  // Query impianti per autocomplete
  const plantsQuery = useQuery({
    queryKey: ['plants-autocomplete', plantInputValue],
    queryFn: async () => {
      const params = { limit: 30, attivo: true };
      if (plantInputValue && plantInputValue.length >= 2) {
        params.search = plantInputValue;
      }
      const res = await plantsApi.getAll(params);
      return res.data?.data || res.data?.plants || [];
    },
    staleTime: 30000
  });

  // Query contatori dell'impianto selezionato
  const metersQuery = useQuery({
    queryKey: ['plant-meters', selectedPlant?.id],
    queryFn: async () => {
      // Usa l'endpoint esistente per ottenere i contatori dell'impianto
      const res = await plantsApi.getMeters(selectedPlant.id);
      return {
        meters: res.data?.data || res.data?.meters || [],
        plant: res.data?.plant || {},
        levelEstimate: res.data?.levelEstimate || null
      };
    },
    enabled: !!selectedPlant?.id
  });

  // Estrai i dati dalla query
  const plantInfo = metersQuery.data?.plant || {};
  const levelEstimate = metersQuery.data?.levelEstimate || null;

  // Ordina i contatori
  const meters = useMemo(() => {
    const metersRaw = metersQuery.data?.meters || [];
    const sorted = [...metersRaw].sort((a, b) => {
      let aValue, bValue;
      
      switch (orderBy) {
        case 'sotto_zona':
          aValue = parseInt(a.sotto_zona || a.c_sotto_zo || a.c_nu_conta) || 0;
          bValue = parseInt(b.sotto_zona || b.c_sotto_zo || b.c_nu_conta) || 0;
          break;
        case 'meter_number':
          // Ordinamento numerico per il numero contatore
          aValue = parseInt(a.meter_number) || 0;
          bValue = parseInt(b.meter_number) || 0;
          break;
        case 'customer_name':
          aValue = (a.customer_name || '').toLowerCase();
          bValue = (b.customer_name || '').toLowerCase();
          break;
        case 'insoluto_cliente':
          aValue = parseFloat(a.insoluto_cliente) || 0;
          bValue = parseFloat(b.insoluto_cliente) || 0;
          break;
        case 'ultima_lettura_mc':
          aValue = parseFloat(a.ultima_lettura_mc) || 0;
          bValue = parseFloat(b.ultima_lettura_mc) || 0;
          break;
        case 'consumo_medio_mc':
          aValue = parseFloat(a.consumo_medio_mc) || 0;
          bValue = parseFloat(b.consumo_medio_mc) || 0;
          break;
        default:
          aValue = a[orderBy];
          bValue = b[orderBy];
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [metersQuery.data?.meters, orderBy, order]);

  // Mutation per salvare le letture
  const saveMutation = useMutation({
    mutationFn: (data) => meterReadingsApi.saveBatch(data),
    onSuccess: (res) => {
      setSnackbar({ 
        open: true, 
        message: res.data?.message || `Salvate ${Object.keys(readings).length} letture con successo`, 
        severity: 'success' 
      });
      queryClient.invalidateQueries({ queryKey: ['plant-meters'] });
      setReadings({});
      setDateDialogOpen(false);
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore nel salvataggio', 
        severity: 'error' 
      });
    }
  });

  // Handler cambio lettura
  const handleReadingChange = useCallback((meterId, value) => {
    setReadings(prev => {
      const newReadings = { ...prev };
      if (value === null || value === undefined) {
        delete newReadings[meterId];
      } else {
        newReadings[meterId] = value;
      }
      return newReadings;
    });
  }, []);

  // Conta letture inserite
  const readingsCount = Object.keys(readings).length;

  // Handler apertura dialogo conferma salvataggio
  const handleOpenSaveDialog = () => {
    if (readingsCount === 0) {
      setSnackbar({ open: true, message: 'Nessuna lettura da salvare', severity: 'warning' });
      return;
    }
    setDataLettura(new Date().toISOString().split('T')[0]);
    setDateDialogOpen(true);
  };

  // Handler salvataggio effettivo
  const handleConfirmSave = () => {
    console.log('[TIPO_LETTURA] ========== SALVATAGGIO LETTURE ==========');
    console.log('[TIPO_LETTURA] readings state completo:', JSON.stringify(readings, null, 2));
    const readingsToSave = Object.entries(readings)
      .filter(([, reading]) => reading !== null && reading !== undefined && reading.value !== null && reading.value !== undefined)
      .map(([meterId, reading]) => {
        console.log('[TIPO_LETTURA] Mapping meter:', meterId, '| reading.tipoLettura:', reading.tipoLettura, '| reading completo:', reading);
        const mapped = {
          meter_id: parseInt(meterId),
          nuova_lettura_mc: parseFloat(reading.value),
          data_lettura: dataLettura,
          tipo_lettura: reading.tipoLettura || 'reale',
          da_fatturare: reading.daFatturare !== undefined ? reading.daFatturare : true
        };
        console.log('[TIPO_LETTURA] Risultato mapping:', mapped);
        return mapped;
      });
    
    console.log('[TIPO_LETTURA] ========== PAYLOAD FINALE ==========');
    console.log('[TIPO_LETTURA] readingsToSave:', JSON.stringify(readingsToSave, null, 2));

    if (readingsToSave.length === 0 && !livelloPercentuale && !livelloLitri) {
      setSnackbar({ open: true, message: 'Inserire almeno una lettura o un livello', severity: 'warning' });
      setDateDialogOpen(false);
      return;
    }

    saveMutation.mutate({
      readings: readingsToSave,
      // Data lettura usata come riferimento - il backend calcolerà il periodo
      data_lettura: dataLettura,
      operatore: 'utente',
      // Livello impianto (se inserito) - usa la stessa data delle letture
      livello_impianto: livelloPercentuale || livelloLitri ? {
        plant_id: selectedPlant.id,
        livello_percentuale: livelloPercentuale ? parseFloat(livelloPercentuale) : null,
        livello_litri: livelloLitri ? parseFloat(livelloLitri) : null,
        data_lettura: dataLettura  // Stessa data delle letture
      } : null
    });
  };

  // Calcola statistiche e stima livello in tempo reale
  const stats = useMemo(() => {
    if (!meters || meters.length === 0) return { totalMeters: 0, litri: 0, importo: 0 };

    let litri = 0;
    let importo = 0;

    meters.forEach(meter => {
      const reading = readings[meter.id];
      if (reading !== null && reading !== undefined && reading.value !== null && reading.value !== undefined) {
        const consumoMc = reading.value - parseFloat(meter.ultima_lettura_mc || 0);
        const consumoLitri = consumoMc * MC_TO_LITERS;
        litri += consumoLitri;
        
        if (meter.prezzo_litro) {
          const gas = consumoLitri * parseFloat(meter.prezzo_litro);
          const iva = gas * (parseFloat(meter.aliquota_iva || 22) / 100);
          importo += gas + iva;
        }
      }
    });

    return { 
      totalMeters: meters.length, 
      litri, 
      importo 
    };
  }, [meters, readings]);

  // Province disponibili: usa le province globali
  const availableProvince = useMemo(() => {
    return globalProvince.sort();
  }, [globalProvince]);

  // Handler apertura dialog stampa
  const handleOpenPrintDialog = () => {
    // Se c'è un impianto selezionato, mostra opzione per impianto
    // Altrimenti, vai direttamente su provincia
    if (selectedPlant) {
      setPrintMode('plant');
    } else {
      setPrintMode('provincia');
    }
    setPrintProvincia(availableProvince[0] || '');
    setPrintDialogOpen(true);
  };

  // Handler stampa tabulato - ora apre la preview
  const handlePrintReadingSheet = async () => {
    try {
      setIsLoadingPreview(true);
      setIsPrinting(true);
      
      const params = { showInsoluto: showInsoluto ? '1' : '0' };
      if (printMode === 'plant') {
        params.plant_id = selectedPlant.id;
      } else {
        params.provincia = printProvincia;
      }

      const response = await meterReadingsApi.getReadingSheetData(params);
      
      if (response.data?.success) {
        setPreviewData(response.data.data);
        setPreviewParams(params);
        setPrintDialogOpen(false);
        setPreviewOpen(true);
      } else {
        throw new Error(response.data?.message || 'Errore nel caricamento dati');
      }
    } catch (err) {
      console.error('Errore caricamento preview tabulato:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore nel caricamento dell\'anteprima', 
        severity: 'error' 
      });
    } finally {
      setIsPrinting(false);
      setIsLoadingPreview(false);
    }
  };

  // Refresh dati preview (dopo aggiunta/rimozione note)
  const handleRefreshPreview = async () => {
    if (!previewParams) return;
    try {
      const response = await meterReadingsApi.getReadingSheetData(previewParams);
      if (response.data?.success) {
        setPreviewData(response.data.data);
      }
    } catch (err) {
      console.error('Errore refresh preview:', err);
    }
  };

  // Calcola stima livello in tempo reale (stima backend - consumi inseriti)
  const stimaLivelloRealTime = useMemo(() => {
    if (!levelEstimate || levelEstimate.error) return null;
    
    const stimaBackend = levelEstimate.stima_attuale_litri || 0;
    const consumiInseriti = stats.litri || 0;
    const stimaAggiornata = stimaBackend - consumiInseriti;
    const capacity = levelEstimate.capacita_totale || 0;
    const stimaPercentuale = capacity > 0 ? (stimaAggiornata / capacity * 100) : 0;
    
    // Calcola discrepanza se è stata inserita una rilevazione
    let discrepanza = null;
    if (livelloLitri) {
      const livelloRilevato = parseFloat(livelloLitri) || 0;
      discrepanza = {
        litri: livelloRilevato - stimaAggiornata,
        percentuale: capacity > 0 ? ((livelloRilevato - stimaAggiornata) / capacity * 100) : 0,
        isAnomaly: Math.abs(livelloRilevato - stimaAggiornata) > Math.max(capacity * 0.05, 200)
      };
    }
    
    return {
      stimaOriginale: stimaBackend,
      consumiInseriti,
      stimaAggiornata,
      stimaPercentuale,
      discrepanza
    };
  }, [levelEstimate, stats.litri, livelloLitri]);

  return (
    <Box>
      {/* Selezione Impianto */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="primary" />
          Seleziona Impianto
        </Typography>
        
        <Autocomplete
          value={selectedPlant}
          onChange={(event, newValue) => {
            setSelectedPlant(newValue);
            setReadings({}); // Reset letture quando cambia impianto
            setLivelloPercentuale(''); // Reset livello
            setLivelloLitri('');
          }}
          inputValue={plantInputValue}
          onInputChange={(event, newInputValue) => {
            setPlantInputValue(newInputValue);
          }}
          options={plantsQuery.data || []}
          getOptionLabel={(option) => {
            if (!option) return '';
            return `${option.plant_code || option.delivery_code || ''} - ${option.indirizzo || ''}, ${option.citta || ''}`;
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          loading={plantsQuery.isLoading}
          noOptionsText={plantInputValue.length < 2 ? "Digita almeno 2 caratteri..." : "Nessun impianto trovato"}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body1" fontWeight="medium">
                  {option.plant_code || option.delivery_code}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <LocationOnIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                  {option.indirizzo}, {option.citta} ({option.provincia})
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cerca impianto per codice o indirizzo"
              placeholder="Es: IMP001 oppure Via Roma..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {plantsQuery.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {/* Info impianto selezionato */}
        {selectedPlant && (
          <Alert severity="info" icon={<LocationOnIcon />}>
            <Typography variant="body2">
              <strong>Impianto:</strong> {selectedPlant.plant_code || selectedPlant.delivery_code} - 
              {selectedPlant.indirizzo}, {selectedPlant.citta} ({selectedPlant.provincia})
            </Typography>
          </Alert>
        )}

        {/* Pulsante Stampa Tabulato - sempre visibile */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={handleOpenPrintDialog}
            disabled={availableProvince.length === 0 && !selectedPlant}
          >
            Stampa Tabulato Letture
          </Button>
        </Box>
      </Paper>

      {/* Nessun impianto selezionato */}
      {!selectedPlant && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="medium">
            👆 Seleziona un impianto per visualizzare i contatori
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cerca per codice impianto (es: IMP001) o per indirizzo (es: Via Roma).
            Una volta selezionato l'impianto, potrai inserire le nuove letture per ogni contatore.
          </Typography>
        </Alert>
      )}

      {/* Loading contatori */}
      {metersQuery.isLoading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Caricamento contatori...
          </Typography>
        </Box>
      )}

      {/* Errore caricamento */}
      {metersQuery.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Errore nel caricamento dei contatori: {metersQuery.error?.message}
        </Alert>
      )}

      {/* Lista contatori */}
      {selectedPlant && metersQuery.data && (
        <>
          {/* Statistiche e bottone salva */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SpeedIcon color="primary" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Contatori
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {readingsCount} / {stats.totalMeters}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Consumo Totale
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      {formatNumber(stats.litri, 0)} L
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EuroIcon color="primary" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Importo Stimato
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatCurrency(stats.importo)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Aggiorna">
                    <IconButton 
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['plant-meters'] })}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Stampa Tabulato Letture">
                    <IconButton 
                      onClick={handleOpenPrintDialog}
                      color="secondary"
                    >
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleOpenSaveDialog}
                    disabled={saveMutation.isPending || readingsCount === 0}
                    fullWidth
                  >
                    Salva ({readingsCount})
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Rilevazione Livello Impianto */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.main' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PropaneTankIcon color="info" />
              Stima e Rilevazione Livello Serbatoio
            </Typography>
            
            {/* Box Stima in tempo reale */}
            {levelEstimate && !levelEstimate.error && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Grid container spacing={2}>
                  {/* Ultima rilevazione */}
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">Ultima Rilevazione</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {levelEstimate.ultima_rilevazione 
                        ? `${formatNumber(levelEstimate.ultima_rilevazione.litri, 0)} L (${formatNumber(levelEstimate.ultima_rilevazione.percentuale, 1)}%)`
                        : 'Nessuna'
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {levelEstimate.ultima_rilevazione 
                        ? formatDate(levelEstimate.ultima_rilevazione.data)
                        : '-'
                      }
                    </Typography>
                  </Grid>
                  
                  {/* Rifornimenti + Ordini */}
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">+ Rifornimenti</Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      +{formatNumber(levelEstimate.rifornimenti_dal_rilevazione, 0)} L
                    </Typography>
                    {levelEstimate.ordini_in_arrivo > 0 && (
                      <Typography variant="caption" color="warning.main">
                        📦 {formatNumber(levelEstimate.ordini_in_arrivo, 0)} L in arrivo
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Consumi fatturati + inseriti */}
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">- Consumi</Typography>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      -{formatNumber(levelEstimate.consumi_fatturati + (stimaLivelloRealTime?.consumiInseriti || 0), 0)} L
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fatturati: {formatNumber(levelEstimate.consumi_fatturati, 0)} L
                      {stimaLivelloRealTime?.consumiInseriti > 0 && (
                        <> | Inseriti: {formatNumber(stimaLivelloRealTime.consumiInseriti, 0)} L</>
                      )}
                    </Typography>
                  </Grid>
                  
                  {/* Stima attuale */}
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">= Stima Attuale</Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold" 
                      color={(stimaLivelloRealTime?.stimaPercentuale || 0) < levelEstimate.soglia_minima ? 'error.main' : 'primary.main'}
                    >
                      {formatNumber(stimaLivelloRealTime?.stimaAggiornata || levelEstimate.stima_attuale_litri, 0)} L
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({formatNumber(stimaLivelloRealTime?.stimaPercentuale || levelEstimate.stima_attuale_percentuale, 1)}%)
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* Barra di progresso visiva */}
                <Box sx={{ mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(100, Math.max(0, stimaLivelloRealTime?.stimaPercentuale || levelEstimate.stima_attuale_percentuale || 0))}
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: (stimaLivelloRealTime?.stimaPercentuale || 0) < levelEstimate.soglia_minima 
                          ? 'error.main' 
                          : (stimaLivelloRealTime?.stimaPercentuale || 0) < 30 
                            ? 'warning.main' 
                            : 'success.main'
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="error.main">Soglia {levelEstimate.soglia_minima}%</Typography>
                    <Typography variant="caption">Capacità: {formatNumber(levelEstimate.capacita_totale, 0)} L</Typography>
                  </Box>
                </Box>
              </Paper>
            )}
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                  Inserisci la rilevazione effettiva:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Confronteremo con la stima per rilevare anomalie
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Livello rilevato (%)"
                  type="number"
                  value={livelloPercentuale}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLivelloPercentuale(val);
                    // Calcola automaticamente i litri se c'è la capacità
                    if (val && plantInfo?.capacita_totale) {
                      setLivelloLitri(Math.round((parseFloat(val) / 100) * plantInfo.capacita_totale).toString());
                    } else {
                      setLivelloLitri('');
                    }
                  }}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                  placeholder="Es: 45"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Livello rilevato (Litri)"
                  type="number"
                  value={livelloLitri}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLivelloLitri(val);
                    // Calcola automaticamente la percentuale se c'è la capacità
                    if (val && plantInfo?.capacita_totale) {
                      setLivelloPercentuale(((parseFloat(val) / plantInfo.capacita_totale) * 100).toFixed(1));
                    } else {
                      setLivelloPercentuale('');
                    }
                  }}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">L</InputAdornment>
                  }}
                  placeholder="Es: 1500"
                />
              </Grid>
            </Grid>
            
            {/* Alert discrepanza */}
            {stimaLivelloRealTime?.discrepanza && (
              <Alert 
                severity={stimaLivelloRealTime.discrepanza.isAnomaly ? 'warning' : 'success'} 
                sx={{ mt: 2 }}
                icon={stimaLivelloRealTime.discrepanza.isAnomaly ? <WarningIcon /> : <CheckCircleIcon />}
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Confronto Stima vs Rilevazione:
                  </Typography>
                  <Typography variant="body2">
                    Stima: <strong>{formatNumber(stimaLivelloRealTime.stimaAggiornata, 0)} L</strong> | 
                    Rilevato: <strong>{formatNumber(livelloLitri, 0)} L</strong> | 
                    Scostamento: <strong style={{ color: stimaLivelloRealTime.discrepanza.isAnomaly ? '#ed6c02' : '#2e7d32' }}>
                      {stimaLivelloRealTime.discrepanza.litri > 0 ? '+' : ''}{formatNumber(stimaLivelloRealTime.discrepanza.litri, 0)} L
                      ({stimaLivelloRealTime.discrepanza.percentuale > 0 ? '+' : ''}{formatNumber(stimaLivelloRealTime.discrepanza.percentuale, 1)}%)
                    </strong>
                  </Typography>
                  {stimaLivelloRealTime.discrepanza.isAnomaly && (
                    <Typography variant="caption" color="warning.main">
                      ⚠️ Scostamento significativo - Verificare eventuali perdite o errori di lettura
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}
            
            {(livelloPercentuale || livelloLitri) && !stimaLivelloRealTime?.discrepanza && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Verrà registrato il livello: <strong>{livelloPercentuale || '-'}%</strong> ({formatNumber(livelloLitri, 0)} litri)
              </Alert>
            )}
          </Paper>

          {/* Tabella contatori */}
          {meters.length === 0 ? (
            <Alert severity="warning">
              Nessun contatore attivo trovato per questo impianto.
            </Alert>
          ) : (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 50 }}>
                        <Tooltip title="Storico letture">
                          <HistoryIcon fontSize="small" />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 70 }}>
                        <TableSortLabel
                          active={orderBy === 'sotto_zona'}
                          direction={orderBy === 'sotto_zona' ? order : 'asc'}
                          onClick={() => handleRequestSort('sotto_zona')}
                          sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                        >
                          Progr.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={orderBy === 'meter_number'}
                          direction={orderBy === 'meter_number' ? order : 'asc'}
                          onClick={() => handleRequestSort('meter_number')}
                          sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                        >
                          Contatore
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={orderBy === 'customer_name'}
                          direction={orderBy === 'customer_name' ? order : 'asc'}
                          onClick={() => handleRequestSort('customer_name')}
                          sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                        >
                          Cliente
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                        <TableSortLabel
                          active={orderBy === 'insoluto_cliente'}
                          direction={orderBy === 'insoluto_cliente' ? order : 'asc'}
                          onClick={() => handleRequestSort('insoluto_cliente')}
                          sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                        >
                          Insoluto
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                        <TableSortLabel
                          active={orderBy === 'ultima_lettura_mc'}
                          direction={orderBy === 'ultima_lettura_mc' ? order : 'asc'}
                          onClick={() => handleRequestSort('ultima_lettura_mc')}
                          sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                        >
                          Ultima Lettura
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                        <TableSortLabel
                          active={orderBy === 'consumo_medio_mc'}
                          direction={orderBy === 'consumo_medio_mc' ? order : 'asc'}
                          onClick={() => handleRequestSort('consumo_medio_mc')}
                          sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                        >
                          Media Bim.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nuova Lettura</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Fatt.</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Consumo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Importo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {meters.map(meter => (
                      <MeterRow 
                        key={meter.id}
                        meter={meter}
                        reading={readings[meter.id]}
                        onReadingChange={handleReadingChange}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Dialog conferma data lettura */}
      <Dialog 
        open={dateDialogOpen} 
        onClose={() => setDateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon color="primary" />
            Conferma Salvataggio Letture
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            Stai per salvare <strong>{readingsCount} letture</strong> per l'impianto 
            <strong> {selectedPlant?.plant_code || selectedPlant?.delivery_code}</strong>.
            {(livelloPercentuale || livelloLitri) && (
              <>
                <br />
                <PropaneTankIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                Livello serbatoio: <strong>{livelloPercentuale || '-'}%</strong> ({formatNumber(livelloLitri, 0)} L)
              </>
            )}
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            In che data sono state rilevate le letture?
          </Typography>
          <TextField
            type="date"
            fullWidth
            label="Data delle letture"
            value={dataLettura}
            onChange={(e) => setDataLettura(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split('T')[0]
            }}
          />

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Il consumo viene calcolato automaticamente dalla differenza con la lettura precedente.
              <br />
              <strong>Tipo lettura</strong> e <strong>Da fatturare</strong> sono impostabili per ogni singola lettura nella tabella.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDateDialogOpen(false)}
            disabled={saveMutation.isPending}
          >
            Annulla
          </Button>
          <Button 
            variant="contained" 
            onClick={handleConfirmSave}
            disabled={saveMutation.isPending || !dataLettura}
            startIcon={saveMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saveMutation.isPending ? 'Salvataggio...' : 'Conferma e Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Stampa Tabulato */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrintIcon color="primary" />
            Stampa Tabulato Letture
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Genera un PDF con l'elenco dei contatori attivi, organizzato per provincia e città,
            con spazio per annotare le nuove letture. I clienti con insoluto saranno evidenziati.
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Tipo di stampa:</Typography>
            <RadioGroup
              value={printMode}
              onChange={(e) => setPrintMode(e.target.value)}
            >
              <FormControlLabel 
                value="plant" 
                control={<Radio />} 
                label={`Per impianto corrente (${selectedPlant?.plant_code || selectedPlant?.id || '-'})`}
                disabled={!selectedPlant}
              />
              <FormControlLabel 
                value="provincia" 
                control={<Radio />} 
                label="Per provincia"
                disabled={availableProvince.length === 0}
              />
            </RadioGroup>
          </FormControl>

          {printMode === 'provincia' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Seleziona Provincia</InputLabel>
              <Select
                value={printProvincia}
                onChange={(e) => setPrintProvincia(e.target.value)}
                label="Seleziona Provincia"
              >
                {availableProvince.map(prov => (
                  <MenuItem key={prov} value={prov}>{prov}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={showInsoluto}
                onChange={(e) => setShowInsoluto(e.target.checked)}
                color="primary"
              />
            }
            label="Mostra colonna Insoluti"
            sx={{ mt: 2, display: 'block' }}
          />

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Verrà mostrata un'anteprima interattiva del tabulato dove potrai aggiungere note di intervento.
              Poi potrai generare il PDF con le note evidenziate.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting}
          >
            Annulla
          </Button>
          <Button 
            variant="contained" 
            onClick={handlePrintReadingSheet}
            disabled={isPrinting || (printMode === 'provincia' && !printProvincia)}
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
          >
            {isPrinting ? 'Caricamento...' : 'Anteprima Tabulato'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Tabulato Letture */}
      <ReadingSheetPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={previewData}
        printParams={previewParams}
        onDataRefresh={handleRefreshPreview}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReadingEntryTab;
